import React, { useState, useEffect, useCallback, useRef } from 'react';
import { resolveUrl, api } from '../lib/api';

const ROTATION_INTERVAL = 12000; // 12 seconds
const PARALLAX = 0.35;

const LEFT_SIDEBAR_VW = 14;
const RIGHT_SIDEBAR_VW = 14;
const TOP_BANNER_PCT = 27;

const SiteWrapper = ({ children, habillages = [] }) => {
  const [isXl, setIsXl] = useState(false);
  const [topH, setTopH] = useState(0);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [fade, setFade] = useState(true);
  const bgRef = useRef(null);
  const firstRatio = habillages[0]?.ratio || 1000 / 1800;

  const calc = useCallback(() => {
    const vw = window.innerWidth;
    setIsXl(vw >= 1280);
    setTopH(vw * firstRatio * (TOP_BANNER_PCT / 100));
  }, [firstRatio]);

  useEffect(() => {
    calc();
    window.addEventListener('resize', calc);
    return () => window.removeEventListener('resize', calc);
  }, [calc]);

  // Rotation timer
  useEffect(() => {
    if (!isXl || habillages.length < 2) return;
    const timer = setInterval(() => {
      setFade(false);
      setTimeout(() => {
        setCurrentIndex((prev) => (prev + 1) % habillages.length);
        setFade(true);
      }, 500);
    }, ROTATION_INTERVAL);
    return () => clearInterval(timer);
  }, [isXl, habillages.length]);

  const current = habillages[currentIndex % habillages.length];

  useEffect(() => {
    if (!isXl || !habillages.length || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return undefined;
    let raf = 0;
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const y = Math.min(window.scrollY, window.innerHeight * 2);
        if (bgRef.current) bgRef.current.style.transform = `translate3d(0, ${y * PARALLAX}px, 0)`;
      });
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => { window.removeEventListener('scroll', onScroll); cancelAnimationFrame(raf); };
  }, [isXl, habillages.length]);

  useEffect(() => {
    if (!isXl || !current?.id) return;
    const key = `dealrun_hab_imp_${current.id}`;
    if (sessionStorage.getItem(key)) return;
    sessionStorage.setItem(key, '1');
    api.post(`/habillages/${current.id}/impression`).catch(() => {});
  }, [isXl, current?.id]);

  if (!isXl || habillages.length === 0) {
    return <div className="min-h-screen bg-black">{children}</div>;
  }

  const trackClick = () => api.post(`/habillages/${current.id}/click`).catch(() => {});

  return (
    <div className="min-h-screen relative">
      {/* Background habillage images — stacked for crossfade, moved slower than the page (parallax) */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none" data-testid="habillage-parallax">
      <div ref={bgRef} className="absolute inset-0 will-change-transform">
      {habillages.map((hab, idx) => (
        <div
          key={hab.id || idx}
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage: `url(${resolveUrl(hab.image)})`,
            backgroundSize: '100% auto',
            backgroundPosition: 'top center',
            backgroundRepeat: 'no-repeat',
            backgroundColor: hab.bg_color,
            opacity: idx === currentIndex % habillages.length && fade ? 1 : 0,
            transition: 'opacity 0.8s ease-in-out',
            zIndex: 0,
          }}
        />
      ))}
      </div>
      </div>

      {/* Clickable overlay on habillage areas (only when a sponsor link is set) */}
      {current.link ? (
        <a
          href={current.link}
          target="_blank"
          rel="noopener noreferrer sponsored"
          onClick={trackClick}
          className="absolute inset-0 z-[1] cursor-pointer"
          aria-label={current.sponsor || current.alt}
          data-testid="habillage-link"
          style={{ pointerEvents: 'auto' }}
        />
      ) : (
        <div className="absolute inset-0 z-[1]" aria-hidden data-testid="habillage-overlay" />
      )}

      {/* Content layer (transparent to clicks except the content column) */}
      <div className="relative z-[2]" style={{ pointerEvents: 'none' }}>
        {/* Top spacer to reveal the habillage banner */}
        <div style={{ height: topH }} />

        {/* Flex layout: transparent left | white content | transparent right */}
        <div className="flex min-h-screen" style={{ pointerEvents: 'none' }}>
          {/* Left transparent sidebar */}
          <div
            className="flex-shrink-0"
            style={{ width: `${LEFT_SIDEBAR_VW}vw`, pointerEvents: 'none' }}
          />

          {/* White content */}
          <div className="flex-1 min-w-0 bg-black min-h-screen" style={{ pointerEvents: 'auto' }}>
            {children}
          </div>

          {/* Right transparent sidebar */}
          <div
            className="flex-shrink-0"
            style={{ width: `${RIGHT_SIDEBAR_VW}vw`, pointerEvents: 'none' }}
          />
        </div>
      </div>
    </div>
  );
};

export default SiteWrapper;
